import React, { useState } from 'react';
import moment from 'moment';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import UserService from '@services/GeniebookAPI/UserServices.services';

export interface NotificationItemProps {
  id: number;
  title: string;
  description: string;
  date: moment.Moment;
  icon: React.ReactNode;
  action: false | (() => void);
  close: () => void;
  isClicked: boolean;
}

const NotificationItem = (props: NotificationItemProps) => {
  const [duration] = useState<moment.Duration>(
    moment.duration(props.date.diff(moment())),
  );
  const queryClient = useQueryClient();
  const trackNotificationMutation = useMutation({
    mutationFn: () => UserService.trackNotification(props.id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['notification'] });
    },
  });
  async function handleNotificationClick() {
    if (props.action !== false) {
      await trackNotificationMutation.mutate();
      props.action();
      props.close();
    }
  }

  const needClick = props.action !== false && !props.isClicked;

  return (
    <div
      className={
        'flex gap-2 w-full p-2 rounded-lg relative overflow-hidden ' +
        (props.action !== false ? 'cursor-pointer ' : '') +
        (needClick ? 'bg-primary-skyblue-faded' : '')
      }
      onClick={handleNotificationClick}
      data-testid={'item-container'}
    >
      <div className={'flex-none w-8'} data-testid={'notification-icon'}>
        {props.icon}
      </div>
      <div className={'flex-grow'}>
        <p
          className={
            'font-bold leading-5 mt-[-2px] mb-1 text-neutral-pencil text-sm ' +
            (needClick ? '!text-primary-neptune-dark' : '')
          }
          data-testid={'notification-title'}
        >
          {props.title}
        </p>
        <p
          className={'text-neutral-pencil leading-5 mb-1 text-xs font-normal'}
          dangerouslySetInnerHTML={{ __html: props.description }}
          data-testid={'notification-description'}
        />
        <p
          className={
            'text-xs font-normal ' +
            (needClick ? 'text-[#476DB3]' : 'text-neutral-dust')
          }
        >
          {duration.humanize(true)}
        </p>
      </div>
      {needClick && (
        <div
          className={
            'absolute w-2 h-2 rounded-full bg-primary-skyblue right-2 top-2'
          }
        />
      )}
    </div>
  );
};

export default NotificationItem;
